"use client";
import React, { useState, useEffect } from "react";
import { createPortal } from 'react-dom';
import { usePathname } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import NavLinkItem from './NavLinkItem';
import { useNavDrawer } from '../hooks/useNavDrawer';
import { ConnectWalletButton } from './ConnectWalletButton';
import { useEvmWallet } from '../../components/EvmWalletProvider';
import WalletSearchBar from './WalletSearchBar';
import { useAuth } from './auth/AuthContext';
import { ThemeToggle } from './ThemeToggle';
import { WalletAvatar } from './WalletIdenticon';
import {
  IconHome, IconSearch, IconFund, IconDonate, IconTrending,
  IconActions, IconSignIn, IconMenuSpecial, IconPhoneBook
} from './NavbarIcons';
import { BellIcon, SettingsIcon } from '../../components/shared/icons';

// Icon and NavLinkItem extracted to separate modules (NavbarIcons.tsx and NavLinkItem.tsx)

type NavItem = { href: string; label: string; Icon?: React.FC; show?: boolean; hint?: string };

// Reqrium IA: the center nav surfaces the six pillars named in the brand
// spec (Home / Wallet Analysis / Charities / Fundraisers / Live Feed /
// Phone Book) directly; everything else (Trending, URL Threat Scan, Donate,
// admin-only links) still exists and still routes correctly - it just lives
// in the "More" overflow menu so the primary bar doesn't get crowded.
//
// `hint` is only rendered in menu surfaces (the "More" dropdown and the
// mobile drawer), never in the pill rail - it turns a bare list of route
// names into a scannable directory without widening the bar itself.
const PRIMARY_NAV: NavItem[] = [
  { href: '/', label: 'Home', Icon: IconHome, hint: 'Overview and live signals' },
  { href: '/wallet-analysis', label: 'Wallet Analysis', Icon: IconSearch, hint: 'Trace and score any wallet' },
  { href: '/charities', label: 'Charities', Icon: IconFund, hint: 'Verified giving directory' },
  { href: '/fundraisers', label: 'Fundraisers', Icon: IconFund, hint: 'Active campaigns' },
  { href: '/transactions/live', label: 'Live Feed', Icon: IconTrending, hint: 'Real-time chain activity' },
  { href: '/phone-book', label: 'Phone Book', Icon: IconPhoneBook, hint: 'Saved wallet contacts' },
];

const SECONDARY_NAV: NavItem[] = [
  { href: '/trending', label: 'Trending', Icon: IconTrending, hint: 'Most-watched wallets' },
  { href: '/url-threat-scan', label: 'URL Threat Scan', Icon: IconSearch, hint: 'Check a link before you click' },
  { href: '/donate', label: 'Donate', Icon: IconDonate, hint: 'Support verified causes' },
];

const COMPACT_PRIMARY_NAV = PRIMARY_NAV.slice(0, 4);
const OVERFLOW_PRIMARY_NAV = PRIMARY_NAV.slice(4);

// variant is accepted for future visual variants but currently unused
/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
export default function Navbar({ variant: _variant }: { variant?: string } = {}) {
  // FIX-36: open/close state, focus trap, and Escape/outside-click handling
  // for the mobile drawer now live in useNavDrawer (app/hooks/useNavDrawer.ts).
  const { open, setOpen, drawerRef } = useNavDrawer();
  const [moreOpen, setMoreOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [btnHover, setBtnHover] = useState(false);
  const [btnPressed, setBtnPressed] = useState(false);
  // Scroll-aware chrome: the bar sits light and airy over the hero, then
  // condenses (tighter padding, solid surface, brand hairline) once the
  // page moves - the standard premium-site navbar behaviour. Driven by a
  // data attribute so all the visual work stays in CSS.
  const [scrolled, setScrolled] = useState(false);
  // FIX-18 (PERF-02): auth/me/admin-check are fetched once, globally, by
  // AuthProvider (app/components/auth/AuthContext.tsx) instead of here. This
  // local state mirrors the context value but allows an instant local
  // override from the 'bp411:nft-avatar-url' event (dispatched on /profile
  // when the user links/clears their NFT avatar) without waiting on a
  // network round-trip.
  const { authenticated: isAuthenticated, isAdmin, nftAvatarUrl: ctxNftAvatarUrl, address: myAddress } = useAuth();
  const [nftAvatarUrl, setNftAvatarUrl] = useState<string | null>(null);
  const { disconnect } = useEvmWallet();
  // call hook unconditionally to satisfy rules-of-hooks
  const pathname = usePathname() || '';

  useEffect(() => {
    setNftAvatarUrl(ctxNftAvatarUrl);
  }, [ctxNftAvatarUrl]);

  // Close the mobile menu on navigation so it can't trap pointer events on #content.
  React.useEffect(() => {
    setOpen(false);
    setMoreOpen(false);
    setNotifOpen(false);
    // setOpen comes from useNavDrawer() rather than a useState call in this
    // component, so eslint can't infer it's a stable setter the way it does
    // for plain useState setters - it is one (React guarantees setState
    // identity across renders), so listing it here just satisfies the lint
    // rule without changing behavior.
  }, [pathname, setOpen]);

  // FIX-32: close the desktop "More" dropdown on outside click / Escape.
  const moreRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!moreOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setMoreOpen(false);
    }
    function onPointerDown(e: MouseEvent) {
      if (moreRef.current && e.target instanceof Node && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [moreOpen]);

  // Notifications dropdown: same outside-click/Escape pattern as "More".
  const notifRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!notifOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setNotifOpen(false);
    }
    function onPointerDown(e: MouseEvent) {
      if (notifRef.current && e.target instanceof Node && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [notifOpen]);

  // Keep the "My NFT" avatar chip in sync when the user links/clears it on /profile.
  useEffect(() => {
    function onNftAvatarUpdated(e: Event) {
      const ce = e as CustomEvent<{ nftAvatarUrl?: string | null }>;
      const next = ce?.detail?.nftAvatarUrl;
      if (next === null) {
        setNftAvatarUrl(null);
        return;
      }
      if (typeof next === 'string') {
        const trimmed = next.trim();
        setNftAvatarUrl(trimmed ? trimmed : null);
      }
    }

    try {
      window.addEventListener('bp411:nft-avatar-url', onNftAvatarUpdated);
      return () => window.removeEventListener('bp411:nft-avatar-url', onNftAvatarUpdated);
    } catch {
      return;
    }
  }, []);

  // FIX-32: Admin already gets its own dedicated pill/badge link further down
  // (desktop + mobile), so it isn't duplicated as a flat list entry anymore.
  // Admin Actions has no other home, so it's appended into the "More" menu,
  // admin-only.
  const secondaryNavItems: NavItem[] = isAdmin
    ? [...SECONDARY_NAV, { href: '/admin-actions', label: 'Admin Actions', Icon: IconActions, hint: 'Moderation queue' }]
    : SECONDARY_NAV;

  const isActive = (href: string) => {
    if (!pathname) return false;
    if (href === '/') return pathname === '/';
    return pathname === href || pathname.startsWith(href + '/') || pathname.startsWith(href);
  };
  const activePrimaryLabel = PRIMARY_NAV.find((item) => isActive(item.href))?.label ?? 'Overview';
  const desktopOverflowItems: NavItem[] = [...OVERFLOW_PRIMARY_NAV, ...secondaryNavItems];
  const navRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const element = navRef.current;
    if (!element) return;

    const root = document.documentElement;
    const updateHeight = () => {
      root.style.setProperty('--site-nav-height', `${Math.ceil(element.getBoundingClientRect().height)}px`);
    };

    updateHeight();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateHeight) : null;
    observer?.observe(element);
    window.addEventListener('resize', updateHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateHeight);
      root.style.removeProperty('--site-nav-height');
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setScrolled(window.scrollY > 12);
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  function proxiedAvatarSrc(src: string) {
    const s = String(src || '');
    if (!s) return s;
    // Avoid browser privacy warnings by fetching Cloudinary images via our origin.
    if (s.startsWith('https://res.cloudinary.com/')) {
      return `/_next/image?url=${encodeURIComponent(s)}&w=96&q=75`;
    }
    return s;
  }

  // NavLinkItem is imported from ./NavLinkItem to keep markup consistent across desktop+mobile

  return (
    <>
      <nav ref={navRef} role="navigation" aria-label="Main" className="site-nav" data-scrolled={scrolled}>
        <span className="pr-nav-underline" aria-hidden="true" />
        <div className="pr-nav-shell mx-auto flex min-h-[4.5rem] w-full max-w-[92rem] flex-wrap items-center justify-between gap-x-3 gap-y-3 px-4 py-3 md:px-6 xl:px-8">
        <div className="flex min-w-0 items-center gap-3">
            <Link href="/" className="flex min-w-0 items-center gap-3 focus:outline-none" aria-label="Go to homepage">
            {/* Brand mark: the redrawn R-in-eye (reqrium-eye-mark.svg).
                Replaces reqrium-eye-logo.svg, a 118x72 hairline eye whose R
                dissolved below ~32px. This one is built square on a dark
                tile with a gradient rim, the iris is clipped inside the eye
                so it reads as an eye rather than a disc behind an outline,
                and the R is knocked out of the iris — solid mass instead of
                thin strokes, which is what survives being shrunk. Same
                artwork drives the favicon and the app icons. */}
            <Image src="/svg/logo/reqrium-eye-mark.svg" alt="Reqrium Logo" width={38} height={38} priority />
            <span className="min-w-0">
              <span
                className="block text-lg font-bold tracking-tight md:text-xl"
                style={{
                  background: "linear-gradient(90deg, #00F5FF 0%, #147CFF 40%, #7A35FF 75%, #E65CFF 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Reqrium
              </span>
              <span className="hidden text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 xl:block">
                Trust intelligence for blockchain operations
              </span>
            </span>
          </Link>
        </div>

        <div className="hidden shrink-0 items-center gap-2 md:flex lg:gap-3">
          <ConnectWalletButton />
          <ThemeToggle className="ml-1" />

          <div className="relative hidden lg:block" ref={notifRef}>
            <button
              type="button"
              onClick={() => setNotifOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={notifOpen}
              aria-label="Notifications"
              className="pr-icon-btn"
            >
              <BellIcon size={18} />
            </button>
            {notifOpen && (
              <div
                role="menu"
                aria-label="Notifications"
                className="pr-menu absolute right-0 top-full mt-2.5 w-80 p-4 z-[1150]"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-slate-100">Notifications</span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-100">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                    Live
                  </span>
                </div>
                <hr className="pr-rule my-3" />
                <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-text, #94a3b8)' }}>
                  You&apos;re all caught up. Threat signals and watchlist alerts will show up here.
                </p>
              </div>
            )}
          </div>

          <div>
            {isAuthenticated ? (
              <button
                onClick={async () => {
                  try {
                    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include', cache: 'no-store' });
                  } catch {}
                  try {
                    await disconnect();
                  } catch {}
                  try {
                    window.localStorage.removeItem('wallet');
                  } catch {}
                  try {
                    window.dispatchEvent(new Event('auth-changed'));
                  } catch {}
                  try {
                    window.location.href = '/';
                  } catch {
                    // ignore
                  }
                }}
                className="pr-nav-pill"
                aria-label="Sign out"
              >
                <span className="pr-nav-icon" aria-hidden="true">
                  <IconSignIn />
                </span>
                <span className="hidden xl:inline">Sign out</span>
              </button>
            ) : (
              <NavLinkItem href="/login" label="Sign in" Icon={IconSignIn} />
            )}
          </div>

          {/* Primary conversion CTA. The nav previously had no visual
              hierarchy - every control was the same weight, so nothing
              pulled the eye. This is the single gradient element in the
              bar, which is what makes the rest of it read as calm. */}
          <Link href="/wallet-analysis" className="pr-btn pr-btn-primary pr-btn-sm hidden lg:inline-flex">
            <span>Analyze Wallet</span>
          </Link>

          <Link
            href="/profile/edit"
            aria-label="Settings"
            className="pr-icon-btn hidden lg:inline-flex"
          >
            <SettingsIcon size={18} />
          </Link>
          {isAdmin && (
            <Link
              href="/admin"
              className="group relative hidden items-center gap-2 rounded-full border border-[#7C3AED]/35 bg-[#7C3AED]/[0.10] px-3 py-1 transition-colors hover:border-[#7C3AED]/55 hover:bg-[#7C3AED]/[0.18] lg:inline-flex"
              aria-label="Open admin dashboard"
              title="Admin mode — in progress"
            >
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#7C3AED]/25 text-[11px] font-semibold text-[#e9d5ff]">
                A
              </span>
              <span className="hidden text-[11px] font-semibold uppercase tracking-[0.16em] text-[#e9d5ff] group-hover:text-white xl:inline">
                Admin
              </span>
              <span className="pointer-events-none absolute left-1/2 top-full z-[1300] flex -translate-x-1/2 translate-y-2 items-center gap-1.5 whitespace-nowrap rounded-md bg-[#111827] px-3 py-1.5 text-[11px] font-medium text-[#e9d5ff] opacity-0 shadow-xl ring-1 ring-[#7C3AED]/40 transition-opacity group-hover:opacity-100">
                <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#7C3AED]/30 text-[9px] text-white">
                  &#10003;
                </span>
                <span>Admin mode &mdash; in progress</span>
              </span>
            </Link>
          )}
          <Link
            href="/profile"
            className="group inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-black/30 px-1.5 py-1 transition-colors hover:bg-emerald-500/10"
            aria-label="Open profile"
          >
            <div
              className="relative overflow-hidden rounded-full flex-shrink-0"
              style={{ width: '2.1rem', height: '2.1rem', background: 'radial-gradient(circle at 30% 0%, rgba(34,197,94,0.75), rgba(2,6,23,1))' }}
            >
              <WalletAvatar address={myAddress} avatarUrl={nftAvatarUrl ? proxiedAvatarSrc(nftAvatarUrl) : null} className="h-full w-full object-cover" alt="My NFT avatar" />
            </div>
            <span className="hidden pr-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#00d4ff] group-hover:text-white xl:inline">
              My NFT
            </span>
          </Link>
        </div>

        {/* Desktop navigation row */}
        {/* FIX: this used to be two separately-bordered flex children (the
            pill-badge+links box, and a "w-full" More+Search box). The second
            box's forced w-full meant it wrapped onto its own visually
            separate bordered row at almost every desktop width between md
            and xl, which read as a broken/disjointed nav ("floating boxes")
            instead of one bar. Everything now lives inside a single glass
            container so it's always one cohesive navbar, even if its
            contents wrap internally on narrower widths. */}
        <div className="hidden w-full items-center border-t border-white/6 pt-3 md:order-3 md:flex xl:order-none xl:flex-1 xl:border-t-0 xl:pt-0">
          <div className="pr-nav-rail flex min-w-0 flex-1 flex-wrap gap-y-2 px-2.5 py-1.5">
          <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/25 bg-cyan-400/[0.14] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-100">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-300 opacity-70" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-300" />
            </span>
            {activePrimaryLabel}
          </span>
          <span className="mx-1 hidden h-6 w-px shrink-0 self-center bg-white/10 2xl:block" aria-hidden="true" />
          <ul className="hidden list-none items-center gap-1 m-0 p-0 2xl:flex">
            {PRIMARY_NAV.map((item) => {
              const active = isActive(item.href);
              return (
                <li key={item.href}>
                  <NavLinkItem href={item.href} label={item.label} Icon={item.Icon} active={active} />
                </li>
              );
            })}
          </ul>
          <ul className="flex list-none flex-wrap items-center gap-1 m-0 p-0 2xl:hidden">
            {COMPACT_PRIMARY_NAV.map((item) => {
              const active = isActive(item.href);
              return (
                <li key={item.href}>
                  <NavLinkItem href={item.href} label={item.label} Icon={item.Icon} active={active} />
                </li>
              );
            })}
          </ul>

          {/* FIX-32: secondary routes live behind a "More" overflow menu so the
              primary bar doesn't get crowded as routes are added. Now nested
              inside the same glass bar (pushed right via ml-auto) instead of
              a sibling box that forced a line break. */}
          <div className="ml-auto flex min-w-0 flex-1 flex-wrap items-center justify-end gap-3 sm:flex-nowrap">
            <div className="relative shrink-0" ref={moreRef}>
              <button
                type="button"
                onClick={() => setMoreOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={moreOpen}
                data-active={moreOpen}
                className="pr-nav-pill"
              >
                <span>More</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className={`h-3.5 w-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </button>
              {moreOpen && (
                <div
                  role="menu"
                  aria-label="More navigation links"
                  className="pr-menu absolute right-0 top-full mt-2.5 w-[19rem] p-2 z-[1150]"
                >
                  <div className="pr-menu-heading">Everything else</div>
                  <ul className="flex flex-col gap-0.5 m-0 list-none p-0">
                    {desktopOverflowItems.map((item) => (
                      <li key={item.href}>
                        <NavLinkItem
                          href={item.href}
                          label={item.label}
                          Icon={item.Icon}
                          hint={item.hint}
                          active={isActive(item.href)}
                          onClick={() => setMoreOpen(false)}
                          mobile
                        />
                      </li>
                    ))}
                  </ul>
                  <hr className="pr-rule my-2" />
                  <Link
                    href="/wallet-analysis"
                    onClick={() => setMoreOpen(false)}
                    className="pr-btn pr-btn-primary pr-btn-sm w-full"
                  >
                    <span>Start a wallet analysis</span>
                  </Link>
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1 sm:max-w-[15rem] lg:max-w-[18rem] xl:w-[18rem] xl:max-w-[20vw] 2xl:w-[21rem] 2xl:max-w-[24vw]">
              <WalletSearchBar size="sm" />
            </div>
          </div>
          </div>
        </div>

        {/* Mobile menu button */}
        {/* Decluttered to match the client's reference mockup: logo + a
            couple of icons + menu, not a six-icon row. Search and
            notifications are still fully available - they now live inside
            the drawer (search bar at the top, notifications via the bell
            still on desktop) instead of duplicating triggers up here. */}
        <div className="md:hidden flex items-center gap-2.5">
          <span className="hidden rounded-full border border-cyan-400/20 bg-cyan-400/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-100 xs:inline-flex">
            Live signals
          </span>
          <ThemeToggle />
          <Link
            href="/profile"
            className="inline-flex items-center justify-center rounded-full border border-emerald-400/35 bg-black/40 p-1.5"
            aria-label="Open profile"
          >
            <div
              className="relative rounded-full overflow-hidden flex-shrink-0"
              style={{ width: '1.9rem', height: '1.9rem', background: 'radial-gradient(circle at 30% 0%, rgba(34,197,94,0.75), rgba(2,6,23,1))' }}
            >
              {/* FIX-49: see desktop pill above for the WalletAvatar/identicon rationale. */}
              <WalletAvatar address={myAddress} avatarUrl={nftAvatarUrl ? proxiedAvatarSrc(nftAvatarUrl) : null} className="w-full h-full object-cover" alt="My NFT avatar" />
            </div>
          </Link>
          <button
            aria-label="Toggle menu"
            aria-expanded={open}
            aria-controls="mobile-drawer"
            onClick={() => setOpen((v) => !v)}
            onMouseEnter={() => setBtnHover(true)}
            onMouseLeave={() => { setBtnHover(false); setBtnPressed(false); }}
            onMouseDown={() => setBtnPressed(true)}
            onMouseUp={() => setBtnPressed(false)}
            className={`p-2 rounded-lg focus:outline-none transition-all duration-180 inline-flex items-center justify-center ${open ? 'bg-[#5B7FFF]/[0.14] ring-2 ring-[#00D4FF]/35 scale-105' : btnHover ? 'bg-white/6 ring-1 ring-white/10' : 'hover:bg-white/3'}`}
            style={{ transform: btnPressed ? 'scale(0.96) translateY(1px)' : undefined }}
          >
            <span className="sr-only">Toggle navigation</span>
            <span className={`inline-block transform transition-transform duration-220 ${open ? 'rotate-12' : btnHover ? 'rotate-4' : ''}` }>
              <IconMenuSpecial state={open ? 'open' : btnHover ? 'hover' : 'default'} />
            </span>
          </button>
        </div>

        {/* ── Mobile navigation sheet ──────────────────────────────────────
            Rebuilt 2026-08-04 (client). Previously a small floating dropdown
            card: rows were under the 44px touch-target minimum, every route
            sat in one flat list, the page scrolled behind it, and it could
            run past the bottom of the viewport on shorter phones.

            It is now a sheet with a fixed header, its own scroll region, and
            a pinned action footer, so the primary CTA is always reachable no
            matter how long the route list gets. Routes are grouped
            (Explore / More / Account) with headings, the current route is
            marked, and rows carry the same one-line hints as the desktop
            "More" menu. Page scroll is locked while it's open (see
            useNavDrawer) and the footer respects the iOS home-indicator
            inset. Rendered in a portal so it can't be clipped by an
            ancestor's stacking context. */}
        {open && typeof document !== 'undefined' ? createPortal(
          <>
            <div className="pr-sheet-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />

            <div
              id="mobile-drawer"
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-label="Site navigation"
              className="pr-sheet"
            >
              <div className="pr-sheet-head">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-base font-semibold leading-tight text-slate-100">Navigation</div>
                    <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Trust intelligence workspace
                    </div>
                  </div>
                  <button onClick={() => setOpen(false)} aria-label="Close menu" className="pr-icon-btn shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mt-3">
                  <WalletSearchBar size="sm" />
                </div>
              </div>

              <div className="pr-sheet-body">
                {isAdmin && (
                  <div className="pr-sheet-group">
                    <div className="pr-menu-heading">Admin</div>
                    <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                      <li className="pr-sheet-item">
                        <NavLinkItem
                          href="/admin"
                          label="Admin Dashboard"
                          active={isActive('/admin')}
                          onClick={() => setOpen(false)}
                          mobile
                        />
                      </li>
                    </ul>
                  </div>
                )}

                <div className="pr-sheet-group">
                  <div className="pr-menu-heading">Explore</div>
                  <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                    {PRIMARY_NAV.map((item, index) => (
                      <li
                        key={item.href}
                        className="pr-sheet-item"
                        style={{ animationDelay: `${40 + index * 28}ms` }}
                      >
                        {/* Client, 2026-08-05: no icon well and no hint line
                            in the sheet. Those were what made each row two
                            lines tall, so eight routes filled the screen. A
                            plain label list is both simpler and easier to
                            scan - the icons stay on the desktop rail, where
                            there's room for them. */}
                        <NavLinkItem
                          href={item.href}
                          label={item.label}
                          active={isActive(item.href)}
                          onClick={() => setOpen(false)}
                          mobile
                        />
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pr-sheet-group">
                  <div className="pr-menu-heading">More</div>
                  <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                    {secondaryNavItems.map((item, index) => (
                      <li
                        key={item.href}
                        className="pr-sheet-item"
                        style={{ animationDelay: `${40 + (PRIMARY_NAV.length + index) * 28}ms` }}
                      >
                        <NavLinkItem
                          href={item.href}
                          label={item.label}
                          active={isActive(item.href)}
                          onClick={() => setOpen(false)}
                          mobile
                        />
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pr-sheet-group">
                  <div className="pr-menu-heading">Account</div>
                  <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                    <li className="pr-sheet-item">
                      <NavLinkItem
                        href="/profile"
                        label="My Profile"
                        active={isActive('/profile')}
                        onClick={() => setOpen(false)}
                        mobile
                      />
                    </li>
                    <li className="pr-sheet-item">
                      {isAuthenticated ? (
                        <button
                          onClick={async () => {
                            try {
                              await fetch('/api/auth/logout', { method: 'POST', credentials: 'include', cache: 'no-store' });
                            } catch {}
                            try {
                              await disconnect();
                            } catch {}
                            try {
                              window.localStorage.removeItem('wallet');
                            } catch {}
                            try {
                              window.dispatchEvent(new Event('auth-changed'));
                            } catch {}
                            try {
                              setOpen(false);
                            } catch {}
                            try {
                              window.location.href = '/';
                            } catch {}
                          }}
                          className="pr-menu-item w-full text-left"
                        >
                          <span className="min-w-0 flex-1">
                            <span className="pr-menu-label block">Sign out</span>
                          </span>
                        </button>
                      ) : (
                        <NavLinkItem
                          href="/login"
                          label="Sign in"
                          onClick={() => setOpen(false)}
                          mobile
                        />
                      )}
                    </li>
                  </ul>
                </div>
              </div>

              <div className="pr-sheet-foot">
                <Link
                  href="/wallet-analysis"
                  onClick={() => setOpen(false)}
                  className="pr-btn pr-btn-primary w-full"
                >
                  <span>Analyze a wallet</span>
                </Link>
              </div>
            </div>
          </>,
          document.body
        ) : null}

        </div>
      </nav>
    <style jsx>{`
    /* Positioning + surface. The pill/menu visuals now live in the shared
       premium layer (styles/globals.css: .pr-nav-pill, .pr-menu, .pr-btn),
       so this block only carries what's genuinely navbar-specific: the
       fixed placement and the two-state background the scroll listener
       toggles via [data-scrolled]. */
    .site-nav { position: fixed; top: 0; left: 0; right: 0; z-index: 1100; }
    .site-nav {
      background: linear-gradient(180deg, rgba(3,8,23,0.72), rgba(3,8,23,0.40));
      box-shadow: 0 1px 0 rgba(255,255,255,0.03) inset;
      backdrop-filter: blur(16px) saturate(135%);
      -webkit-backdrop-filter: blur(16px) saturate(135%);
      border-bottom: 1px solid rgba(148,163,184,0.08);
    }
    .site-nav[data-scrolled="true"] {
      background: linear-gradient(180deg, rgba(3,8,23,0.95), rgba(4,10,26,0.86));
      backdrop-filter: blur(24px) saturate(150%);
      -webkit-backdrop-filter: blur(24px) saturate(150%);
    }

    /* LIGHT MODE.
       The two backgrounds above are near-black at 72%/95% opacity. They are
       declared here in styled-jsx rather than in the shared layer, so a rule in
       styles/globals.css cannot reliably override them: styled-jsx appends its
       own scoping class (.site-nav.jsx-xxx, specificity 0-2-0) and injects at
       runtime after the stylesheet link, so it wins ties on source order. The
       light variants therefore have to live in this block.

       Left dark, the bar stayed near-black in light mode while the page behind
       it went white, and the pill labels - which inherit #b8c6de from
       .pr-nav-pill - measured 1.19:1 against it. Every page in the app shows
       this bar, so it was 32 of the failures on its own. */
    [data-theme="light"] .site-nav {
      background: linear-gradient(180deg, rgba(255,255,255,0.86), rgba(248,250,253,0.62));
      box-shadow: 0 1px 0 rgba(15,23,42,0.04) inset;
      border-bottom: 1px solid rgba(15,23,42,0.10);
    }
    [data-theme="light"] .site-nav[data-scrolled="true"] {
      background: linear-gradient(180deg, rgba(255,255,255,0.97), rgba(246,249,253,0.93));
      border-bottom-color: rgba(12,123,214,0.20);
    }
        `}</style>
    </>
  );
}
